import {
    Box,
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

export interface InventoryImageCropDialogProps {
    imageSrc: string;
    fileName: string;
    fileType: string;
    isOpen: boolean;
    onCancel: () => void;
    onConfirm: (file: File) => void;
    /** Dialog title — typically `${name} · ${manufacturerPartNumber}`. */
    title: string;
}

const FULL_IMAGE_CROP: Crop = { unit: '%', x: 0, y: 0, width: 100, height: 100 };

async function cropToFile(
    image: HTMLImageElement,
    crop: PixelCrop,
    fileName: string,
    fileType: string,
): Promise<File> {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelCrop = {
        x: crop.x * scaleX,
        y: crop.y * scaleY,
        width: crop.width * scaleX,
        height: crop.height * scaleY,
    };
    canvas.width = Math.max(1, Math.round(pixelCrop.width));
    canvas.height = Math.max(1, Math.round(pixelCrop.height));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        canvas.width,
        canvas.height,
    );
    const outputType = fileType || 'image/jpeg';
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Failed to crop image'));
                    return;
                }
                resolve(new File([blob], fileName, { type: outputType }));
            },
            outputType,
            0.95,
        );
    });
}

/**
 * Modal that lets the user pick the region of an image that should become
 * the stored item image (Add Item and Import Preview). Aspect ratio is free;
 * the default selection is the full image.
 */
const InventoryImageCropDialog = ({
    imageSrc,
    fileName,
    fileType,
    isOpen,
    onCancel,
    onConfirm,
    title,
}: InventoryImageCropDialogProps) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [isProcessing, setIsProcessing] = useState(false);

    const handleImageLoad: React.ReactEventHandler<HTMLImageElement> = () => {
        setCrop(FULL_IMAGE_CROP);
    };

    const handleUsePhoto = async () => {
        const image = imgRef.current;
        if (!image) return;
        const cropToUse: PixelCrop =
            completedCrop && completedCrop.width > 0 && completedCrop.height > 0
                ? completedCrop
                : {
                      unit: 'px',
                      x: 0,
                      y: 0,
                      width: image.width,
                      height: image.height,
                  };
        setIsProcessing(true);
        try {
            const file = await cropToFile(image, cropToUse, fileName, fileType);
            onConfirm(file);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onCancel} isCentered size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md">{title}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Text fontSize="sm" color="gray.600" mb={3}>
                        Drag the selection to choose the portion of the photo to use.
                    </Text>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bg="gray.50"
                        borderRadius="md"
                        overflow="hidden"
                        maxH="60vh"
                    >
                        <ReactCrop
                            crop={crop}
                            keepSelection
                            onChange={(_, percentCrop) => setCrop(percentCrop)}
                            onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                        >
                            {/* eslint-disable-next-line jsx-a11y/alt-text -- decorative crop source */}
                            <img
                                ref={imgRef}
                                src={imageSrc}
                                onLoad={handleImageLoad}
                                style={{ maxHeight: '55vh', display: 'block' }}
                            />
                        </ReactCrop>
                    </Box>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" onClick={onCancel} isDisabled={isProcessing}>
                        Cancel
                    </Button>
                    <Button
                        bg="#0071E3"
                        color="white"
                        _hover={{ bg: '#3544a0' }}
                        ml={3}
                        onClick={handleUsePhoto}
                        isLoading={isProcessing}
                        loadingText="Processing…"
                    >
                        Use Photo
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default InventoryImageCropDialog;
